import React from "react";

function Cookies() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="bg-white shadow-sm rounded-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">COOKIE POLICY</h1>
          <p className="text-gray-700 font-semibold">
            Effective Date: 01/01/2026 | Last Updated: 01/01/2026
          </p>
        </div>

        {/* Table of Contents */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">TABLE OF CONTENTS</h2>
          <ol className="list-decimal pl-6 space-y-1 text-gray-700">
            <li>INTRODUCTION</li>
            <li>WHAT ARE COOKIES?</li>
            <li>COOKIE CATEGORIES</li>
            <li>HOW WE USE COOKIES</li>
            <li>THIRD-PARTY PARTNERS & VENDORS</li>
            <li>MANAGE YOUR COOKIE PREFERENCES</li>
            <li>COOKIE DATA RETENTION</li>
            <li>COOKIES AND CHILDREN'S PRIVACY</li>
            <li>SECURITY MEASURES</li>
            <li>INTERNATIONAL DATA TRANSFERS</li>
            <li>YOUR RIGHTS & CHOICES</li>
            <li>UPDATES TO THIS POLICY</li>
            <li>CONTACT INFORMATION</li>
            <li>COMPLIANCE CERTIFICATIONS</li>
          </ol>
        </section>

        {/* 1. INTRODUCTION */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">1. INTRODUCTION</h2>
          
          <h3 className="text-xl font-semibold text-blue-700 mb-3">1.1 About This Policy</h3>
          <p className="text-gray-700 mb-4">
            TradeEngine.AI Pte. Ltd. ("TradeEngine.AI," "we," "us," or "our") respects your privacy and is committed to transparency about the technologies we use. This Cookie Policy explains how we use cookies, web beacons, pixels, and similar tracking technologies (collectively "cookies") across our AI-powered trade finance platform.
          </p>

          <p className="text-gray-700 mb-2">This Policy applies to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Our website: www.tradeengine.ai</li>
            <li>Our web-based platform application</li>
            <li>Our mobile applications (iOS and Android)</li>
            <li>Any subdomains and related digital properties</li>
          </ul>

          <p className="text-gray-700 mb-2">Related documents you should read:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Privacy Policy – How we collect and use your personal data</li>
            <li>Terms of Service – Terms governing your use of our Platform</li>
            <li>Data Processing Agreement – For enterprise customers</li>
          </ul>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">1.2 Your Consent</h3>
          <p className="text-gray-700 mb-4">
            When you first visit our Platform, you will see our cookie banner allowing you to accept all cookies, reject non-essential cookies, or customize your preferences. Your choices matter to us, and you can change your preferences anytime through our Cookie Preference Center.
          </p>
        </section>

        {/* 2. WHAT ARE COOKIES? */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">2. WHAT ARE COOKIES?</h2>
          
          <h3 className="text-xl font-semibold text-blue-700 mb-3">2.1 Cookie Basics</h3>
          <p className="text-gray-700 mb-4">
            Cookies are small text files (typically 4KB or less) placed on your device when you visit websites. They serve as a "memory" for websites, helping them recognize you on return visits and remember your preferences.
          </p>

          <p className="text-gray-700 mb-2">Types of cookies by lifespan:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Session Cookies:</strong> Temporary cookies deleted when you close your browser</li>
            <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until manually deleted</li>
          </ul>

          <p className="text-gray-700 mb-2">Types of cookies by origin:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>First-Party Cookies:</strong> Set directly by TradeEngine.AI</li>
            <li><strong>Third-Party Cookies:</strong> Set by our trusted partners and service providers</li>
          </ul>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">2.2 Similar Technologies</h3>
          <p className="text-gray-700 mb-4">Beyond traditional cookies, we use:</p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-blue-300">
              <thead className="bg-blue-100">
                <tr>
                  <th className="border border-blue-300 px-4 py-2 text-left text-gray-700">Technology</th>
                  <th className="border border-blue-300 px-4 py-2 text-left text-gray-700">What It Does</th>
                  <th className="border border-blue-300 px-4 py-2 text-left text-gray-700">Where We Use It</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-300 px-4 py-2">Web Beacons (Pixels)</td>
                  <td className="border border-blue-300 px-4 py-2">Tiny invisible images that track page views and email opens</td>
                  <td className="border border-blue-300 px-4 py-2">Marketing emails, Platform pages</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-4 py-2">Local Storage</td>
                  <td className="border border-blue-300 px-4 py-2">Browser storage for larger data amounts that persists across sessions</td>
                  <td className="border border-blue-300 px-4 py-2">Dashboard preferences, draft data</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-4 py-2">Session Storage</td>
                  <td className="border border-blue-300 px-4 py-2">Temporary browser storage cleared when tab closes</td>
                  <td className="border border-blue-300 px-4 py-2">Form data, temporary calculations</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-4 py-2">Device Fingerprinting</td>
                  <td className="border border-blue-300 px-4 py-2">Collects device characteristics for fraud prevention</td>
                  <td className="border border-blue-300 px-4 py-2">Security systems, authentication</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-4 py-2">Mobile SDKs</td>
                  <td className="border border-blue-300 px-4 py-2">Software code in mobile apps for analytics and functionality</td>
                  <td className="border border-blue-300 px-4 py-2">iOS and Android apps</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-4 py-2">ETags</td>
                  <td className="border border-blue-300 px-4 py-2">HTTP cache validators that can track users</td>
                  <td className="border border-blue-300 px-4 py-2">CDN content delivery</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 3. COOKIE CATEGORIES */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">3. COOKIE CATEGORIES</h2>
          <p className="text-gray-700 mb-4">
            We organize cookies into four categories based on their purpose. Here is what each category does and the impact of accepting or rejecting them:
          </p>

          {/* 3.1 Strictly Necessary Cookies */}
          <h3 className="text-xl font-semibold text-blue-700 mb-3">3.1 STRICTLY NECESSARY COOKIES</h3>
          <p className="text-gray-700 mb-2">
            <strong>Purpose:</strong> Essential for the Platform to function. These cannot be disabled without breaking core functionality.
          </p>

          <p className="text-gray-700 mb-2">What they enable:</p>
          <ul className="list-disc pl-6 mb-3 space-y-1 text-gray-700">
            <li>Secure login and authentication</li>
            <li>Session management and security tokens</li>
            <li>Load balancing across our servers</li>
            <li>Fraud prevention and security monitoring</li>
            <li>Processing your trade finance transactions</li>
            <li>CSRF (Cross-Site Request Forgery) protection</li>
            <li>Compliance with legal obligations</li>
          </ul>

          <p className="text-gray-700 mb-2">
            <strong>Legal basis:</strong> Legitimate interest (essential for service delivery and security)
          </p>
          <p className="text-gray-700 mb-4">
            <strong>Impact if disabled:</strong> <span className="text-red-600">❌ You cannot use the Platform</span>
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-blue-300 text-sm">
              <thead className="bg-blue-100">
                <tr>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Cookie Name</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Purpose</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Duration</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Type</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Domain</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_session_id</td>
                  <td className="border border-blue-300 px-3 py-2">Maintains your authenticated session</td>
                  <td className="border border-blue-300 px-3 py-2">Session</td>
                  <td className="border border-blue-300 px-3 py-2">HTTP-Only</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_csrf_token</td>
                  <td className="border border-blue-300 px-3 py-2">Prevents cross-site request forgery attacks</td>
                  <td className="border border-blue-300 px-3 py-2">Session</td>
                  <td className="border border-blue-300 px-3 py-2">Secure</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_auth_token</td>
                  <td className="border border-blue-300 px-3 py-2">Authenticates API requests</td>
                  <td className="border border-blue-300 px-3 py-2">24 hours</td>
                  <td className="border border-blue-300 px-3 py-2">HTTP-Only</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_security_fingerprint</td>
                  <td className="border border-blue-300 px-3 py-2">Detects suspicious login attempts</td>
                  <td className="border border-blue-300 px-3 py-2">30 days</td>
                  <td className="border border-blue-300 px-3 py-2">Secure</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_load_balancer</td>
                  <td className="border border-blue-300 px-3 py-2">Routes requests to appropriate server</td>
                  <td className="border border-blue-300 px-3 py-2">Session</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_rate_limit</td>
                  <td className="border border-blue-300 px-3 py-2">Prevents abuse and API overuse</td>
                  <td className="border border-blue-300 px-3 py-2">1 hour</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">AWSALB</td>
                  <td className="border border-blue-300 px-3 py-2">AWS Application Load Balancer cookie</td>
                  <td className="border border-blue-300 px-3 py-2">7 days</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">cookieConsent</td>
                  <td className="border border-blue-300 px-3 py-2">Stores your cookie preferences</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 3.2 Functional Cookies */}
          <h3 className="text-xl font-semibold text-blue-700 mb-3">3.2 FUNCTIONAL COOKIES</h3>
          <p className="text-gray-700 mb-2">
            <strong>Purpose:</strong> Enable enhanced features and personalization to improve your experience.
          </p>

          <p className="text-gray-700 mb-2">What they enable:</p>
          <ul className="list-disc pl-6 mb-3 space-y-1 text-gray-700">
            <li>Remembering your language, currency, and timezone preferences</li>
            <li>Saving your dashboard layout and customizations</li>
            <li>Storing your notification preferences</li>
            <li>Auto-filling form data you have previously entered</li>
            <li>Enabling chat and communication features</li>
            <li>Remembering your recently viewed transactions</li>
          </ul>

          <p className="text-gray-700 mb-2">
            <strong>Legal basis:</strong> Consent (optional enhancements)
          </p>
          <p className="text-gray-700 mb-4">
            <strong>Impact if disabled:</strong> <span className="text-orange-600">⚠️ You will need to reset preferences each visit, and some features will not work optimally</span>
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-blue-300 text-sm">
              <thead className="bg-blue-100">
                <tr>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Cookie Name</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Purpose</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Duration</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Type</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Domain</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_language</td>
                  <td className="border border-blue-300 px-3 py-2">Stores language preference (EN, ZH, MS, etc.)</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_currency</td>
                  <td className="border border-blue-300 px-3 py-2">Remembers display currency (USD, SGD, EUR, etc.)</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_timezone</td>
                  <td className="border border-blue-300 px-3 py-2">Maintains timezone setting</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_dashboard_layout</td>
                  <td className="border border-blue-300 px-3 py-2">Saves dashboard widget arrangement</td>
                  <td className="border border-blue-300 px-3 py-2">6 months</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_theme</td>
                  <td className="border border-blue-300 px-3 py-2">Remembers light/dark mode preference</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_notification_prefs</td>
                  <td className="border border-blue-300 px-3 py-2">Stores notification settings</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">intercom-session</td>
                  <td className="border border-blue-300 px-3 py-2">Powers customer support chat</td>
                  <td className="border border-blue-300 px-3 py-2">7 days</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">.intercom.io</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">intercom-id</td>
                  <td className="border border-blue-300 px-3 py-2">Identifies returning chat users</td>
                  <td className="border border-blue-300 px-3 py-2">9 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">.intercom.io</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 3.3 Analytics & Performance Cookies */}
          <h3 className="text-xl font-semibold text-blue-700 mb-3">3.3 ANALYTICS & PERFORMANCE COOKIES</h3>
          <p className="text-gray-700 mb-2">
            <strong>Purpose:</strong> Help us understand how you use the Platform so we can improve performance, fix bugs, and enhance features.
          </p>

          <p className="text-gray-700 mb-2">What they enable:</p>
          <ul className="list-disc pl-6 mb-3 space-y-1 text-gray-700">
            <li>Understanding which features are most/least used</li>
            <li>Identifying technical errors and performance bottlenecks</li>
            <li>Measuring page load times and system responsiveness</li>
            <li>Testing new features with A/B testing</li>
            <li>Analyzing user journeys and navigation patterns</li>
            <li>Improving our AI recommendation algorithms (anonymized data only)</li>
            <li>Generating usage statistics and reports</li>
          </ul>

          <p className="text-gray-700 mb-2">
            <strong>Legal basis:</strong> Consent (with option to opt-out)
          </p>
          <p className="text-gray-700 mb-4">
            <strong>Impact if disabled:</strong> <span className="text-green-600">✓ No impact on functionality; we just will not know how to improve your experience</span>
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-blue-300 text-sm">
              <thead className="bg-blue-100">
                <tr>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Cookie Name</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Purpose</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Duration</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Type</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Domain</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">_ga</td>
                  <td className="border border-blue-300 px-3 py-2">Distinguishes unique users</td>
                  <td className="border border-blue-300 px-3 py-2">2 years</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Google Analytics</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">_ga_*</td>
                  <td className="border border-blue-300 px-3 py-2">Stores and counts page views</td>
                  <td className="border border-blue-300 px-3 py-2">2 years</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Google Analytics</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">_gid</td>
                  <td className="border border-blue-300 px-3 py-2">Distinguishes unique users</td>
                  <td className="border border-blue-300 px-3 py-2">24 hours</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Google Analytics</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">_gat_UA-*</td>
                  <td className="border border-blue-300 px-3 py-2">Throttles request rate</td>
                  <td className="border border-blue-300 px-3 py-2">1 minute</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Google Analytics</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">te_analytics_id</td>
                  <td className="border border-blue-300 px-3 py-2">Internal analytics tracking</td>
                  <td className="border border-blue-300 px-3 py-2">2 years</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_feature_usage</td>
                  <td className="border border-blue-300 px-3 py-2">Tracks feature adoption</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">mp_*</td>
                  <td className="border border-blue-300 px-3 py-2">Mixpanel analytics identifiers</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Mixpanel</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">_hjid</td>
                  <td className="border border-blue-300 px-3 py-2">Hotjar user identification</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Hotjar</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">ajs_user_id</td>
                  <td className="border border-blue-300 px-3 py-2">Segment user identification</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Segment</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">ajs_anonymous_id</td>
                  <td className="border border-blue-300 px-3 py-2">Segment anonymous tracking</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Segment</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 3.4 Marketing & Advertising Cookies */}
          <h3 className="text-xl font-semibold text-blue-700 mb-3">3.4 MARKETING & ADVERTISING COOKIES</h3>
          <p className="text-gray-700 mb-2">
            <strong>Purpose:</strong> Deliver relevant advertisements and measure marketing campaign effectiveness.
          </p>

          <p className="text-gray-700 mb-2">What they enable:</p>
          <ul className="list-disc pl-6 mb-3 space-y-1 text-gray-700">
            <li>Showing you relevant ads on third-party websites</li>
            <li>Preventing you from seeing the same ad repeatedly</li>
            <li>Measuring which marketing channels bring users to our Platform</li>
            <li>Understanding which campaigns drive conversions</li>
            <li>Creating lookalike audiences for advertising</li>
            <li>Retargeting website visitors with relevant content</li>
            <li>Tracking affiliate and referral program performance</li>
          </ul>

          <p className="text-gray-700 mb-2">
            <strong>Legal basis:</strong> Consent (fully optional)
          </p>
          <p className="text-gray-700 mb-4">
            <strong>Impact if disabled:</strong> <span className="text-green-600">✓ No impact on functionality; you will still see ads but they will be less relevant</span>
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-blue-300 text-sm">
              <thead className="bg-blue-100">
                <tr>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Cookie Name</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Purpose</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Duration</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Type</th>
                  <th className="border border-blue-300 px-3 py-2 text-left text-gray-700">Domain</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">_fbp</td>
                  <td className="border border-blue-300 px-3 py-2">Facebook browser tracking</td>
                  <td className="border border-blue-300 px-3 py-2">3 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Meta/Facebook</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">fr</td>
                  <td className="border border-blue-300 px-3 py-2">Facebook advertising delivery</td>
                  <td className="border border-blue-300 px-3 py-2">3 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Meta/Facebook</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">_gcl_au</td>
                  <td className="border border-blue-300 px-3 py-2">Google conversion tracking</td>
                  <td className="border border-blue-300 px-3 py-2">3 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Google Ads</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">IDE</td>
                  <td className="border border-blue-300 px-3 py-2">Google DoubleClick ad targeting</td>
                  <td className="border border-blue-300 px-3 py-2">13 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Google Ads</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">li_sugr</td>
                  <td className="border border-blue-300 px-3 py-2">LinkedIn browser identifier</td>
                  <td className="border border-blue-300 px-3 py-2">3 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">LinkedIn</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">bcookie</td>
                  <td className="border border-blue-300 px-3 py-2">LinkedIn browser identification</td>
                  <td className="border border-blue-300 px-3 py-2">1 year</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">LinkedIn</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">_uetsid</td>
                  <td className="border border-blue-300 px-3 py-2">Microsoft Bing tracking</td>
                  <td className="border border-blue-300 px-3 py-2">1 day</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">Microsoft Ads</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">te_utm_source</td>
                  <td className="border border-blue-300 px-3 py-2">Tracks marketing campaign source</td>
                  <td className="border border-blue-300 px-3 py-2">30 days</td>
                  <td className="border border-blue-300 px-3 py-2">First-party</td>
                  <td className="border border-blue-300 px-3 py-2">.tradeengine.ai</td>
                </tr>
                <tr>
                  <td className="border border-blue-300 px-3 py-2">hubspotutk</td>
                  <td className="border border-blue-300 px-3 py-2">HubSpot visitor tracking</td>
                  <td className="border border-blue-300 px-3 py-2">13 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">HubSpot</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-blue-300 px-3 py-2">__hstc</td>
                  <td className="border border-blue-300 px-3 py-2">HubSpot visitor identifier</td>
                  <td className="border border-blue-300 px-3 py-2">13 months</td>
                  <td className="border border-blue-300 px-3 py-2">Third-party</td>
                  <td className="border border-blue-300 px-3 py-2">HubSpot</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. HOW WE USE COOKIES */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">4. HOW WE USE COOKIES</h2>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">4.1 AI & Machine Learning Applications</h3>
          <p className="text-gray-700 mb-4">
            As an AI-powered platform, we use cookies to enhance our machine learning capabilities:
          </p>

          <div className="mb-4">
            <p className="text-gray-700 font-semibold mb-2">Trade Matching & Recommendations:</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Analyzing user preferences to improve trade opportunity matching</li>
              <li>Learning from interaction patterns to refine AI recommendations</li>
              <li>Personalizing risk assessment based on historical behavior patterns</li>
            </ul>
          </div>

          <div className="mb-4">
            <p className="text-gray-700 font-semibold mb-2">Fraud Detection & Security:</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Device fingerprinting to detect anomalous behavior</li>
              <li>Pattern recognition for suspicious transaction identification</li>
              <li>Real-time risk scoring based on session characteristics</li>
            </ul>
          </div>

          <div className="mb-4">
            <p className="text-gray-700 font-semibold mb-2">Platform Optimization:</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>A/B testing new AI features and interface improvements</li>
              <li>Measuring AI prediction accuracy and user satisfaction</li>
              <li>Identifying areas where AI assistance is most valuable</li>
            </ul>
          </div>

          <p className="text-gray-700 bg-blue-50 p-4 rounded border-l-4 border-blue-500">
            <strong>Important:</strong> All AI training uses aggregated, anonymized data. Individual user behavior is never identifiable in our AI models.
          </p>

          <h3 className="text-xl font-semibold text-blue-700 mb-3 mt-6">4.2 Multi-Product Cookie Synchronization</h3>
          <p className="text-gray-700 mb-2">
            If you use multiple TradeEngine.AI services or switch between web and mobile:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Your cookie preferences sync automatically across all platforms</li>
            <li>Session continuity is maintained when switching devices</li>
            <li>Preferences set on one platform apply to all TradeEngine.AI properties</li>
          </ul>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">4.3 Cross-Border Data Processing</h3>
          <p className="text-gray-700 mb-2">Some cookies involve data transfers to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>United States: Google Analytics, AWS, Mixpanel</li>
            <li>European Union: Intercom (Dublin), certain CDN nodes</li>
            <li>Singapore: Primary TradeEngine.AI infrastructure</li>
          </ul>
          <p className="text-gray-700">
            We ensure appropriate safeguards for all international transfers (see Section 10).
          </p>
        </section>

        {/* Continue with remaining sections - I'll provide the key sections */}

        {/* 6. MANAGE YOUR COOKIE PREFERENCES */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">6. MANAGE YOUR COOKIE PREFERENCES</h2>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">6.1 Cookie Preference Center</h3>
          <p className="text-gray-700 mb-2">
            You can manage your cookie preferences at any time through our Cookie Preference Center. Simply click the link in the footer of our website or platform to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Accept or reject specific cookie categories</li>
            <li>View all active cookies in real-time</li>
            <li>Update preferences instantly</li>
            <li>Download your cookie data</li>
            <li>Clear all non-essential cookies</li>
          </ul>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">6.2 Browser-Level Controls</h3>
          <p className="text-gray-700 mb-2">All major browsers allow you to control cookies:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Google Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
            <li><strong>Mozilla Firefox:</strong> Settings → Privacy & Security → Enhanced Tracking Protection</li>
            <li><strong>Safari (Mac/iOS):</strong> Preferences → Privacy → Manage Website Data</li>
            <li><strong>Microsoft Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies</li>
          </ul>
          <p className="text-gray-700 mb-4">
            <strong>Note:</strong> Browser-level blocking may prevent the Platform from functioning correctly. We recommend using our Cookie Preference Center instead for granular control.
          </p>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">6.3 Mobile App Controls</h3>
          <div className="mb-4">
            <p className="text-gray-700 font-semibold mb-2">iOS (iPhone/iPad):</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Settings → Privacy → Tracking</li>
              <li>Toggle off "Allow Apps to Request to Track"</li>
            </ul>
          </div>

          <div className="mb-4">
            <p className="text-gray-700 font-semibold mb-2">Android:</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Settings → Privacy → Ads</li>
              <li>Enable "Opt out of Ads Personalization"</li>
            </ul>
          </div>
        </section>

        {/* 13. CONTACT INFORMATION */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">13. CONTACT INFORMATION</h2>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">13.1 General Inquiries</h3>
          <div className="mb-4 text-gray-700">
            <p><strong>Email:</strong> privacy@tradeengine.ai</p>
            <p><strong>Subject Line:</strong> "Cookie Policy Question"</p>
            <p><strong>Response Time:</strong> Within 5 business days</p>
          </div>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">13.2 Data Protection Officer</h3>
          <div className="mb-4 text-gray-700">
            <p><strong>Email:</strong> dpo@tradeengine.ai</p>
            <p><strong>Mail:</strong> Data Protection Officer, TradeEngine.AI Pte. Ltd., [Singapore Address]</p>
          </div>

          <h3 className="text-xl font-semibold text-blue-700 mb-3">13.3 Supervisory Authorities</h3>
          <p className="text-gray-700 mb-4">
            If you believe our cookie practices violate applicable laws, you may contact:
          </p>

          <div className="space-y-4 text-gray-700">
            <div>
              <p className="font-semibold">Singapore:</p>
              <p>Personal Data Protection Commission</p>
              <p>Website: https://www.pdpc.gov.sg</p>
              <p>Email: info@pdpc.gov.sg</p>
            </div>

            <div>
              <p className="font-semibold">European Union:</p>
              <p>Your local Data Protection Authority</p>
              <p>Directory: https://edpb.europa.eu/about-edpb/board/members</p>
            </div>

            <div>
              <p className="font-semibold">United Kingdom:</p>
              <p>Information Commissioner's Office (ICO)</p>
              <p>Website: https://ico.org.uk</p>
              <p>Helpline: 0303 123 1113</p>
            </div>

            <div>
              <p className="font-semibold">United States (California):</p>
              <p>California Privacy Protection Agency</p>
              <p>Website: https://cppa.ca.gov</p>
            </div>
          </div>
        </section>

        {/* 14. COMPLIANCE CERTIFICATIONS */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">14. COMPLIANCE CERTIFICATIONS</h2>
          <p className="text-gray-700 mb-2">
            TradeEngine.AI's cookie practices are compliant with:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Singapore Personal Data Protection Act (PDPA) 2012</li>
            <li>EU General Data Protection Regulation (GDPR)</li>
            <li>UK Data Protection Act 2018</li>
            <li>California Consumer Privacy Act (CCPA) / California Privacy Rights Act (CPRA)</li>
            <li>ePrivacy Directive (Cookie Law) 2002/58/EC</li>
            <li>ISO 27001:2013 Information Security Management</li>
            <li>SOC 2 Type II Certification</li>
          </ul>
        </section>

        {/* Footer */}
        <div className="border-t-2 border-gray-200 pt-8 mt-8 text-center">
          <p className="text-xl font-bold text-gray-900 mb-4">* * *</p>
          <p className="text-gray-700 font-semibold mb-2">
            This Cookie Policy is effective as of 01/01/2026 and governs all cookie usage on the TradeEngine.AI Platform.
          </p>
          <p className="text-gray-500 text-sm">
            © 2024 TradeEngine.AI Pte. Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Cookies;